/**
 * useMeetingCapture — TASK-665
 *
 * The capture engine extracted from `LiveCockpit.tsx` (977 lines, deleted with the rest
 * of the 21-file / 3,753-line cockpit tree). Everything the cockpit rendered — live
 * transcript, insight rail, proposals — either showed events the backend stopped emitting
 * when TASK-427 deleted the live agents, or asked an RM to read a screen while a client
 * sat in front of them. Only this logic was worth keeping.
 *
 * Extracted with NO behaviour change, then covered by unit tests before anything was
 * deleted. Behaviours that are load-bearing and easy to lose:
 *
 *  - **Bot suppression is two gates, not one.** `botActive` (driven by the `bot_status`
 *    webhook) AND `detectPlatform(meetingUrl)` (a conference URL, known before any webhook
 *    arrives). Without the second gate the browser mic races the bot and fills `utterances`
 *    with Whisper hallucinations ("you", "Thanks for watching") off a silent room mic.
 *  - **The WebSocket opens BEFORE the recorder starts.** Historically that ordering was
 *    what registered the session so the first `/chunk` POST didn't 404. TASK-664 made
 *    registration explicit server-side, so the coupling is gone — but the order is kept
 *    because the socket must be listening before the first chunk is transcribed.
 *  - **No `isRecording` gate on `ondataavailable`.** The MediaRecorder lifecycle is the
 *    source of truth; gating on React state races the final `requestData()` + `stop()`
 *    and drops the partial chunk recorded since the last 30s boundary — the chunk most
 *    worth keeping on pause/end.
 *  - **Pause is stop()+new recorder**, not `MediaRecorder.pause()`, so each chunk is a
 *    clean container the backend can transcode independently.
 *
 * The hook never renders anything and never navigates; `MeetingRecorder` owns the UI and
 * the consent gate. Mounting this hook does NOT touch the microphone — capture begins only
 * when `start()` is called.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { API_CONFIG } from '../config/api-config'
import { detectPlatform } from '../services/conferenceUrlDetection'
import {
  MeetingChunkUploadError,
  uploadMeetingChunk,
} from '../services/api/meetingChunkUpload'
import type { AudioPermission, WebSocketMessage } from '../types/meeting'

// ============================================================================
// Capture configuration (verbatim from LiveCockpit)
// ============================================================================

/**
 * Best supported MediaRecorder mime type. The backend transcodes with ffmpeg before
 * Whisper, so any of these is acceptable.
 */
export function pickMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return ''
  const candidates = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus']
  for (const mt of candidates) {
    if (MediaRecorder.isTypeSupported(mt)) return mt
  }
  return ''
}

export const CHUNK_TIMESLICE_MS = 30_000
const MAX_PARALLEL_UPLOADS = 3

/** Bot lifecycle as reported by the Attendee webhook. */
export type BotState = 'joining' | 'in-call' | 'left' | 'failed' | null

/** Why the browser microphone is not running. Drives honest UI copy. */
export type SuppressionReason =
  | 'bot-owns-audio' // a conference URL or an active bot — never record in parallel
  | null

export type CaptureStatus =
  | 'idle' // mounted, mic untouched
  | 'starting'
  | 'recording'
  | 'paused'
  | 'suppressed' // bot owns the audio
  | 'ending'
  | 'ended'
  | 'error'

export interface UseMeetingCaptureOptions {
  sessionId?: string
  clientId?: string
  advisorId?: string
  accountId?: string
  userId?: string
  userRole?: string
  /** Conference URL. Non-null for a bot-attended meeting → browser mic suppressed. */
  meetingUrl?: string
  /** Subscribe to the live channel without ever capturing (TASK-667 monitor mode). */
  subscribeOnly?: boolean
  onTranscript?: (chunk: { seq: number; text: string }) => void
}

export interface UseMeetingCaptureResult {
  status: CaptureStatus
  audioPermission: AudioPermission
  mediaStream: MediaStream | null
  isConnected: boolean
  botState: BotState
  suppression: SuppressionReason
  /** Seconds of ACTUAL capture — excludes paused time. */
  elapsedSeconds: number
  /** Chunks accepted by the backend vs. those that exhausted their retry. */
  chunksUploaded: number
  failedChunks: number
  /** True while any chunk upload is in flight — "end" must not claim success yet. */
  hasPendingUploads: boolean
  errorMessage: string | null
  start: () => Promise<void>
  pause: () => void
  resume: () => void
  /** Flush the final partial chunk and end the session. Resolves to the end outcome. */
  end: () => Promise<{ ok: boolean; partial: boolean }>
}

export function useMeetingCapture(
  options: UseMeetingCaptureOptions
): UseMeetingCaptureResult {
  const {
    sessionId,
    clientId,
    advisorId,
    accountId,
    userId,
    userRole,
    meetingUrl,
    subscribeOnly = false,
    onTranscript,
  } = options

  const [status, setStatus] = useState<CaptureStatus>('idle')
  const [audioPermission, setAudioPermission] = useState<AudioPermission>('pending')
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [botState, setBotState] = useState<BotState>(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [chunksUploaded, setChunksUploaded] = useState(0)
  const [failedChunks, setFailedChunks] = useState(0)
  const [pendingUploads, setPendingUploads] = useState(0)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const nextSeqRef = useRef(0)
  const inFlightCountRef = useRef(0)
  const uploadAbortRef = useRef<AbortController | null>(null)
  const isMountedRef = useRef(true)
  const endedRef = useRef(false)
  // TASK-665: separate "in progress" guard from "succeeded" latch. `endedRef` must latch
  // ONLY after a successful end, so a failed end can be retried; `endingRef` blocks a
  // reentrant concurrent call without falsely claiming success.
  const endingRef = useRef(false)

  const botActive = botState === 'joining' || botState === 'in-call'
  // A recognized conference URL means the bot owns the audio even before the first
  // bot.state_change webhook lands. Both conditions suppress.
  const urlSuppressed = Boolean(detectPlatform(meetingUrl))
  const suppression: SuppressionReason =
    botActive || urlSuppressed ? 'bot-owns-audio' : null

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    mediaStreamRef.current = mediaStream
  }, [mediaStream])

  // --------------------------------------------------------------------------
  // Elapsed timer — counts only while actually recording, so a paused meeting
  // does not inflate the duration the RM is shown.
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (status !== 'recording') return
    const id = window.setInterval(() => {
      setElapsedSeconds((s) => s + 1)
    }, 1000)
    return () => window.clearInterval(id)
  }, [status])

  // --------------------------------------------------------------------------
  // TASK-665 AC: if bot ownership becomes known AFTER browser capture starts (an in-person
  // recording where a remote participant later joins and the Attendee bot is dispatched), the
  // browser MUST stop recording — never let the mic and the bot write the same session in
  // parallel. `start()` guards the not-yet-recording case; this handles the mid-session flip.
  // We flush the final chunk, stop the mic (light goes off), and keep the socket open so the
  // widget keeps monitoring bot status. We do NOT POST /end: the meeting is not over — the
  // bot's terminal webhook enqueues the (idempotent) batch.
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (suppression !== 'bot-owns-audio') return
    if (status !== 'recording' && status !== 'paused') return
    const recorder = mediaRecorderRef.current
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.requestData() // flush the partial chunk since the last boundary
      } catch {
        /* idempotent */
      }
      try {
        recorder.stop()
      } catch {
        /* idempotent */
      }
    }
    mediaRecorderRef.current = null
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop())
      mediaStreamRef.current = null
    }
    if (isMountedRef.current) {
      setMediaStream(null)
      setStatus('suppressed')
    }
    toast.info('A meeting bot joined and now owns the audio. Browser recording stopped.')
  }, [suppression, status])

  // --------------------------------------------------------------------------
  // Chunk upload
  // --------------------------------------------------------------------------
  const handleChunkAvailable = useCallback(
    async (blob: Blob, sessionIdForUpload: string) => {
      if (blob.size === 0) return

      // Backpressure: drop rather than buffer unbounded, and keep recording so a slow
      // uplink never stops the meeting. Counted as failed — the record IS incomplete.
      if (inFlightCountRef.current >= MAX_PARALLEL_UPLOADS) {
        if (isMountedRef.current) setFailedChunks((n) => n + 1)
        toast.warning('Upload backlog full; a chunk was dropped. Check your connection.')
        return
      }

      const seq = nextSeqRef.current++
      inFlightCountRef.current++
      if (isMountedRef.current) setPendingUploads((n) => n + 1)

      try {
        await uploadMeetingChunk({
          sessionId: sessionIdForUpload,
          seq,
          chunk: blob,
          signal: uploadAbortRef.current?.signal,
          mimeType: blob.type,
        })
        if (isMountedRef.current) setChunksUploaded((n) => n + 1)
      } catch (err) {
        if (err instanceof MeetingChunkUploadError) {
          if (isMountedRef.current) setFailedChunks((n) => n + 1)
          toast.error(`Chunk ${err.seq} failed to upload. Recording continues.`)
        } else if (
          (err as Error)?.name === 'CanceledError' ||
          (err as { code?: string })?.code === 'ERR_CANCELED'
        ) {
          // Caller aborted (unmount / teardown). Silent, and not a data-loss signal.
        } else {
          if (isMountedRef.current) setFailedChunks((n) => n + 1)
        }
      } finally {
        inFlightCountRef.current = Math.max(0, inFlightCountRef.current - 1)
        if (isMountedRef.current) setPendingUploads((n) => Math.max(0, n - 1))
      }
    },
    []
  )

  // --------------------------------------------------------------------------
  // WebSocket — session control + live events
  // --------------------------------------------------------------------------
  const handleMessage = useCallback(
    (data: WebSocketMessage) => {
      switch (data.type) {
        case 'chunk_transcribed': {
          if (typeof data.seq !== 'number') return
          const text = (data.text ?? '').trim()
          if (text) onTranscript?.({ seq: data.seq, text })
          break
        }
        case 'bot_status': {
          const next = (data.state ?? null) as BotState
          setBotState(next)
          if (next === 'in-call') {
            toast.success('Bot is recording the meeting')
          } else if (next === 'failed') {
            toast.error(
              `Bot failed to join${data.error_message ? `: ${data.error_message}` : ''}`
            )
          }
          break
        }
        default:
          // Control/keepalive frames and any legacy live-agent message. Ignored.
          break
      }
    },
    [onTranscript]
  )

  const connectWebSocket = useCallback(() => {
    if (!sessionId) return
    if (wsRef.current) return // idempotent — never open a second socket per session

    const ws = new WebSocket(`${API_CONFIG.WS_GATEWAY_URL}/ws/live-meeting/${sessionId}`)
    wsRef.current = ws

    ws.onopen = () => {
      if (isMountedRef.current) setIsConnected(true)
      // Monitor mode must NOT send session_start: that triggers the server-side
      // start_meeting (validation, registration, broadcast). A watcher only needs the
      // socket open for the Redis→WS bridge (TASK-664 subscribes on accept).
      if (subscribeOnly) return
      ws.send(
        JSON.stringify({
          type: 'session_start',
          sessionId,
          clientId,
          advisorId,
          accountId,
          userId,
          userRole,
        })
      )
    }

    ws.onmessage = (event) => {
      try {
        handleMessage(JSON.parse(event.data) as WebSocketMessage)
      } catch {
        // Malformed frame — ignore rather than tear the socket down.
      }
    }

    ws.onclose = () => {
      if (isMountedRef.current) setIsConnected(false)
    }
  }, [
    sessionId,
    clientId,
    advisorId,
    accountId,
    userId,
    userRole,
    subscribeOnly,
    handleMessage,
  ])

  // Monitor mode connects on mount; capture mode connects only on start().
  useEffect(() => {
    if (subscribeOnly && sessionId) connectWebSocket()
  }, [subscribeOnly, sessionId, connectWebSocket])

  // --------------------------------------------------------------------------
  // Teardown
  // --------------------------------------------------------------------------
  const tearDown = useCallback(() => {
    const recorder = mediaRecorderRef.current
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop()
      } catch {
        /* idempotent */
      }
    }
    mediaRecorderRef.current = null

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop())
      mediaStreamRef.current = null
    }
    if (isMountedRef.current) setMediaStream(null)

    if (uploadAbortRef.current) {
      uploadAbortRef.current.abort()
      uploadAbortRef.current = null
    }
    if (wsRef.current) {
      try {
        wsRef.current.close()
      } catch {
        /* idempotent */
      }
      wsRef.current = null
    }
  }, [])

  useEffect(() => () => tearDown(), [tearDown])

  const attachRecorderHandlers = useCallback(
    (recorder: MediaRecorder, sessionIdForUpload: string) => {
      // Deliberately NO isRecording gate — see the module docstring.
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          void handleChunkAvailable(e.data, sessionIdForUpload)
        }
      }
      recorder.onerror = () => {
        if (!isMountedRef.current) return
        setErrorMessage('Recording stopped unexpectedly.')
        setStatus('error')
      }
    },
    [handleChunkAvailable]
  )

  // --------------------------------------------------------------------------
  // Controls
  // --------------------------------------------------------------------------
  const start = useCallback(async () => {
    if (!sessionId || subscribeOnly) return
    if (status === 'recording' || status === 'starting') return

    setErrorMessage(null)

    // Bot owns the audio: open the socket for status/transcript, never the microphone.
    if (suppression === 'bot-owns-audio') {
      setStatus('suppressed')
      setAudioPermission('granted')
      connectWebSocket()
      return
    }

    if (typeof MediaRecorder === 'undefined') {
      setAudioPermission('denied')
      setErrorMessage('This browser does not support audio recording.')
      setStatus('error')
      return
    }

    setStatus('starting')
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })
      if (!isMountedRef.current) {
        stream.getTracks().forEach((t) => t.stop())
        return
      }

      setAudioPermission('granted')
      setMediaStream(stream)
      mediaStreamRef.current = stream

      stream.getAudioTracks().forEach((track) => {
        track.onended = () => {
          if (!isMountedRef.current) return
          setErrorMessage('Microphone disconnected. End the meeting to save what was captured.')
          setStatus('error')
        }
      })

      const mimeType = pickMimeType()
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream)
      mediaRecorderRef.current = recorder
      uploadAbortRef.current = new AbortController()
      attachRecorderHandlers(recorder, sessionId)

      // Socket first, then capture — the listener must exist before the first chunk.
      connectWebSocket()

      recorder.start(CHUNK_TIMESLICE_MS)
      setStatus('recording')
    } catch (error) {
      const err = error as DOMException
      let message = 'Could not start the microphone.'
      if (err?.name === 'NotAllowedError') {
        message = 'Microphone access denied. Enable it in your browser settings.'
      } else if (err?.name === 'NotFoundError') {
        message = 'No microphone found. Connect one and try again.'
      } else if (err?.name === 'NotReadableError') {
        message = 'The microphone is in use by another application.'
      }
      if (!isMountedRef.current) return
      setAudioPermission('denied')
      setErrorMessage(message)
      setStatus('error')
    }
  }, [
    sessionId,
    subscribeOnly,
    status,
    suppression,
    connectWebSocket,
    attachRecorderHandlers,
  ])

  const pause = useCallback(() => {
    const recorder = mediaRecorderRef.current
    if (!recorder || status !== 'recording') return
    // requestData() before stop() flushes the partial chunk since the last boundary.
    try {
      recorder.requestData()
    } catch {
      /* idempotent */
    }
    try {
      recorder.stop()
    } catch {
      /* idempotent */
    }
    setStatus('paused')
  }, [status])

  const resume = useCallback(() => {
    if (status !== 'paused' || !sessionId) return
    const stream = mediaStreamRef.current
    if (!stream) return
    const mimeType = pickMimeType()
    const next = mimeType
      ? new MediaRecorder(stream, { mimeType })
      : new MediaRecorder(stream)
    mediaRecorderRef.current = next
    attachRecorderHandlers(next, sessionId)
    next.start(CHUNK_TIMESLICE_MS)
    setStatus('recording')
  }, [status, sessionId, attachRecorderHandlers])

  const end = useCallback(async (): Promise<{ ok: boolean; partial: boolean }> => {
    // Already ended successfully — safe to report success without re-posting.
    if (endedRef.current) return { ok: true, partial: failedChunks > 0 }
    // A previous attempt is still running: do NOT claim success (the bug this fixes) — tell
    // the caller it isn't done so it keeps the retry affordance.
    if (endingRef.current) return { ok: false, partial: failedChunks > 0 }
    endingRef.current = true
    setStatus('ending')

    try {
      // Flush the final partial chunk BEFORE stopping.
      const recorder = mediaRecorderRef.current
      if (recorder && recorder.state === 'recording') {
        try {
          recorder.requestData()
        } catch {
          /* idempotent */
        }
        try {
          recorder.stop()
        } catch {
          /* idempotent */
        }
      }

      // Give in-flight uploads a bounded chance to land. "Ended" must not claim success
      // while audio is still unacknowledged.
      const deadline = Date.now() + 10_000
      while (inFlightCountRef.current > 0 && Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 200))
      }
      const unacknowledged = inFlightCountRef.current > 0

      let ok = false
      if (sessionId) {
        try {
          const { httpClient } = await import('../lib/httpClient')
          await httpClient.post(`/ws/live-meeting/end/${sessionId}`)
          ok = true
        } catch (error) {
          const err = error as { response?: { status?: number } }
          // 404 = the session was already finalized elsewhere; not a capture failure.
          ok = err?.response?.status === 404
          if (!ok) {
            setErrorMessage('The meeting could not be closed cleanly. You can retry.')
          }
        }
      }

      // TASK-665: latch "ended" ONLY on success. On failure `endedRef` stays false so the
      // next click actually re-posts the end (and re-enqueues the batch) instead of the old
      // behaviour, which returned {ok:true} for a meeting whose batch was never enqueued.
      if (ok) {
        endedRef.current = true
        // Stop the microphone only once the meeting is truly closed — a failed end keeps the
        // recorder alive so the RM can retry without losing capture.
        tearDown()
      }
      if (isMountedRef.current) setStatus(ok ? 'ended' : 'error')
      return { ok, partial: failedChunks > 0 || unacknowledged }
    } finally {
      endingRef.current = false
    }
  }, [sessionId, failedChunks, tearDown])

  return {
    status,
    audioPermission,
    mediaStream,
    isConnected,
    botState,
    suppression,
    elapsedSeconds,
    chunksUploaded,
    failedChunks,
    hasPendingUploads: pendingUploads > 0,
    errorMessage,
    start,
    pause,
    resume,
    end,
  }
}

export default useMeetingCapture
